import type { SourceType } from "@tandemdiabetes/sugarmate-api-react-client"
import { ConsentTrackingStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Checkbox,
  CheckboxCheckMark,
  LinkOutlineIcon,
  useToasterContext,
  Form,
  ActionSubmit,
} from "@tandemdiabetes/sugarmate-react-components"
import { useNavigate } from "@tanstack/react-router"
import type {
  ChangeEventHandler,
  FormEventHandler,
} from "react"
import {
  useCallback,
  useState,
} from "react"
import { useAddDataSource } from "../useAddDataSource"
import styles from "./DataConsent.module.scss"

import { usePolyglot } from "src/contexts"
import {
  useSources,
  useUpdateConsents,
} from "src/hooks"
import { SavingResponseErrorToast } from "src/toasts/SavingResponseErrorToast"

interface Props {
  isOnboarding?: boolean;
  sourceType: SourceType.Nightscout | SourceType.Dexcom | SourceType.Tandem;
}

const polyglotPrefix = "pages.settings.data_source.privacy_consent."
const noticeOfPrivacyPracticesId = "data-source-privacy-practices"
const noticeOfPrivacyPracticesCheckboxId = `${noticeOfPrivacyPracticesId}-checkbox`

export function DataConsentContent(props: Props) {
  const {
    isOnboarding,
    sourceType,
  } = props

  const { data: sources } = useSources()
  const addDataSource = useAddDataSource(isOnboarding)

  const polyglot = usePolyglot()
  const navigate = useNavigate()

  const setConsent = useUpdateConsents()

  const { showToast } = useToasterContext()

  const [
    acceptedNoticeOfPrivacyPractices,
    setAcceptedNoticeOfPrivacyPractices,
  ] = useState(false)

  const onNoticeOfPrivacyPracticesChange: ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (e) => {
        setAcceptedNoticeOfPrivacyPractices(e.target.checked)
      },
      [setAcceptedNoticeOfPrivacyPractices],
    )

  const acknowledgeConsent = useCallback(
    async () => {
      if (!setConsent) {
        return
      }

      try {
        await setConsent({
          npp_consent: {
            status: acceptedNoticeOfPrivacyPractices
              ? ConsentTrackingStatus.Accepted
              : ConsentTrackingStatus.Declined,
          },
        })

        if (sources?.length) {
          void navigate({
            to: isOnboarding ? "/onboarding/data_source" : "/account/data_source",
          })
        } else {
          addDataSource(sourceType)
        }
      } catch {
        showToast(
          "saving-response-error-toast",
          SavingResponseErrorToast,
        )
      }
    },
    [
      sources,
      sourceType,
      addDataSource,
      isOnboarding,
      navigate,
      setConsent,
      acceptedNoticeOfPrivacyPractices,
      showToast,
    ],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void acknowledgeConsent()
    },
    [acknowledgeConsent],
  )

  return (
    <Form
      className={styles.container}
      onSubmit={onSubmit}
    >
      <div className={styles.heading}>
        {polyglot.t(`${polyglotPrefix}heading`)}
      </div>
      <div className={styles.text}>
        {polyglot.t(`${polyglotPrefix}description`)}
      </div>
      <div
        className={styles.privacyCheckboxContainer}
        data-checked={acceptedNoticeOfPrivacyPractices}
        id={noticeOfPrivacyPracticesId}
      >
        <Checkbox
          className={styles.checkbox}
          htmlFor={noticeOfPrivacyPracticesCheckboxId}
          checked={acceptedNoticeOfPrivacyPractices}
          onChange={onNoticeOfPrivacyPracticesChange}
        >
          <CheckboxCheckMark id={noticeOfPrivacyPracticesCheckboxId} />
          {polyglot.t(`${polyglotPrefix}notice_of_privacy_practices.label`)}
        </Checkbox>
        <a
          href="https://www.tandemdiabetes.com/legal/privacy/hipaa-notice-of-privacy-practices"
          target="_blank"
          rel="noreferrer"
          className={styles.link}
        >
          <LinkOutlineIcon className={styles.linkIcon} />
        </a>
      </div>

      <div className={styles.buttonContainer}>
        {acceptedNoticeOfPrivacyPractices ? (
          <ActionSubmit
            id="confirm-add-button"
            stylePreset="primary"
            className={styles.dialogMultiButton}
            value={polyglot.t(`${polyglotPrefix}confirm_cta`)}
          />
        ) : (
          <ActionSubmit
            id="skip-button"
            stylePreset="secondary"
            className={styles.dialogMultiButton}
            value={polyglot.t(`${polyglotPrefix}skip_cta`)}
          />
        )}
      </div>
    </Form>
  )
}
